import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Flex,
  FlexItem,
  Form,
  FormGroup,
  FormSelect,
  FormSelectOption,
  Label,
  PageSection,
  Skeleton,
  TextInput
} from "@patternfly/react-core";
import { Table, Tbody, Td, Th, Thead, Tr } from "@patternfly/react-table";

import { apiGet, apiPost } from "../../app/api";
import type {
  AccountingBridgeExportResult,
  AccountingBridgePayload,
  AccountingBridgeRow,
  AccountingBridgeStatusResult,
  FinanceClosePayload,
  PeriodLockResult
} from "../../types";
import {
  compactDate,
  compactDateTime,
  dateDaysFromNow,
  financeStatusColor,
  formatNumber,
  readableEventLabel
} from "../../display";

export function FinanceCloseWorkspace({ csrfToken }: { csrfToken: string }) {
  const [closeData, setCloseData] = useState<FinanceClosePayload | null>(null);
  const [bridgeRows, setBridgeRows] = useState<AccountingBridgeRow[]>([]);
  const [bridgeFilter, setBridgeFilter] = useState("open");
  const [selectedBridgeIds, setSelectedBridgeIds] = useState<Set<number>>(new Set());
  const [bridgeStatus, setBridgeStatus] = useState("");
  const [blockedReason, setBlockedReason] = useState("");
  const [periodStart, setPeriodStart] = useState(() => dateDaysFromNow(0));
  const [periodEnd, setPeriodEnd] = useState(() => dateDaysFromNow(0));
  const [lockScope, setLockScope] = useState("inventory");
  const [lockStatus, setLockStatus] = useState("Locked");
  const [lockReason, setLockReason] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadFinance = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [financePayload, bridgePayload] = await Promise.all([
        apiGet<FinanceClosePayload>("/api/finance-close"),
        apiGet<AccountingBridgePayload>("/api/accounting-bridge")
      ]);
      setCloseData(financePayload);
      setBridgeRows(bridgePayload.rows || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Finance Close could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadFinance();
  }, [loadFinance]);

  const filteredBridgeRows = useMemo(() => {
    if (bridgeFilter === "all") return bridgeRows;
    if (bridgeFilter === "open") {
      return bridgeRows.filter(row => ["Ready", "Exported", "Imported", "Blocked"].includes(String(row.status || "")));
    }
    return bridgeRows.filter(row => row.status === bridgeFilter);
  }, [bridgeFilter, bridgeRows]);

  const bridgeSummary = useMemo(() => {
    const ready = bridgeRows.filter(row => row.status === "Ready").length;
    const blocked = bridgeRows.filter(row => row.status === "Blocked").length;
    const eventCount = bridgeRows.reduce((sum, row) => sum + Number(row.accounting_event_count || 0), 0);
    return `${filteredBridgeRows.length} shown. ${ready} ready, ${blocked} blocked, ${eventCount} accounting event rows available.`;
  }, [bridgeRows, filteredBridgeRows.length]);

  const toggleBridgeRow = (id: number): void => {
    setSelectedBridgeIds(current => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const applyBridgeStatus = async (): Promise<void> => {
    if (!bridgeStatus) {
      setError("Choose the accounting response before applying.");
      return;
    }
    if (!selectedBridgeIds.size) {
      setError("Select at least one accounting handoff row.");
      return;
    }
    if (bridgeStatus === "Blocked" && !blockedReason.trim()) {
      setError("Blocked accounting rows need a reason.");
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const result = await apiPost<AccountingBridgeStatusResult>("/api/accounting-bridge/status", {
        ids: Array.from(selectedBridgeIds),
        status: bridgeStatus,
        reason: blockedReason.trim()
      }, csrfToken);
      setNotice(`${result.updated} accounting handoff row(s) marked ${result.status}.`);
      setSelectedBridgeIds(new Set());
      setBlockedReason("");
      await loadFinance();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Accounting status could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const exportBridgeEvents = async (): Promise<void> => {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const payload = await apiGet<AccountingBridgeExportResult>("/api/accounting-bridge/ims-events");
      const ids = (payload.rows || []).map(row => row.id).filter((id): id is number => typeof id === "number");
      if (ids.length) {
        await apiPost<{ ok: boolean; exported: number }>("/api/accounting-bridge/exported", { ids }, csrfToken);
      }
      const config = payload.configuration?.entity_configured ? "entity configured" : "entity not configured";
      setNotice(`Prepared ${payload.events?.length || 0} accounting event row(s); ${config}.`);
      await loadFinance();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Accounting event export could not be prepared.");
    } finally {
      setSaving(false);
    }
  };

  const savePeriodLock = async (): Promise<void> => {
    if (!lockReason.trim()) {
      setError("Enter a reason for the period lock or unlock.");
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const result = await apiPost<PeriodLockResult>("/api/period-locks", {
        period_start: periodStart,
        period_end: periodEnd,
        lock_scope: lockScope,
        status: lockStatus,
        reason: lockReason.trim()
      }, csrfToken);
      setNotice(`${readableEventLabel(result.lock_scope)} period is ${result.status.toLowerCase()} for ${compactDate(result.period_start)} to ${compactDate(result.period_end)}.`);
      setLockReason("");
      await loadFinance();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Period status could not be saved.");
    } finally {
      setSaving(false);
    }
  };

  const readiness = closeData?.readiness || {};
  const checks = closeData?.checks || [];
  const blockers = (closeData?.buckets || []).filter(bucket => Number(bucket.count || 0) > 0 || bucket.severity !== "green");
  const summaryRows = closeData?.accounting_summary || [];
  const lockRows = closeData?.lock_history || [];

  return (
    <PageSection className="pf-toj-workspace" hasBodyWrapper={false}>
      <div id="pf-finance-close-view">
        <div className="pf-toj-workspace-title">
          <h1>Finance Close</h1>
          <p>Review close readiness, send IMS events to accounting, record accounting responses, and lock reviewed periods.</p>
        </div>
        {error && <Alert className="pf-toj-inline-alert" variant="danger" title={error} />}
        {notice && <Alert className="pf-toj-inline-alert" variant="success" title={notice} />}
        {loading ? (
          <div className="pf-toj-skeleton-stack" aria-label="Loading Finance Close">
            <Skeleton height="96px" screenreaderText="Loading finance readiness" />
            <Skeleton height="240px" screenreaderText="Loading accounting handoff rows" />
          </div>
        ) : (
          <div className="pf-toj-finance-grid">
            <Card className="pf-toj-work-card pf-toj-primary-work-card">
              <CardBody>
                <Flex alignItems={{ default: "alignItemsCenter" }} justifyContent={{ default: "justifyContentSpaceBetween" }}>
                  <FlexItem>
                    <h2>Close Readiness</h2>
                    <p className="pf-toj-muted">Checklist for the current operating date.</p>
                  </FlexItem>
                  <FlexItem>
                    <Label color={financeStatusColor(readiness.status)}>{readiness.status || "Open"}</Label>
                  </FlexItem>
                </Flex>
                <div className="pf-toj-finance-summary" aria-label="Finance close totals">
                  <span><strong>{formatNumber(readiness.red || 0)}</strong>Must fix</span>
                  <span><strong>{formatNumber(readiness.yellow || 0)}</strong>Review soon</span>
                  <span><strong>{formatNumber(readiness.green || 0)}</strong>Clear</span>
                </div>
                <div className="pf-toj-table-wrap pf-toj-finance-table-wrap" tabIndex={0}>
                  <Table aria-label="Finance close checklist" variant="compact">
                    <Thead>
                      <Tr>
                        <Th>Close Step</Th>
                        <Th>Status</Th>
                        <Th>Detail</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {checks.length ? checks.map(check => (
                        <Tr key={`${check.label}-${check.detail}`}>
                          <Td dataLabel="Close Step"><strong>{check.label || ""}</strong></Td>
                          <Td dataLabel="Status"><Label color={financeStatusColor(check.status)}>{check.status || "Open"}</Label></Td>
                          <Td dataLabel="Detail">{check.detail || ""}</Td>
                        </Tr>
                      )) : (
                        <Tr><Td colSpan={3}>No finance close checks are available.</Td></Tr>
                      )}
                    </Tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
            <Card className="pf-toj-work-card">
              <CardBody>
                <h2>Close Blockers</h2>
                <p className="pf-toj-muted">Open items that prevent a clean handoff to accounting.</p>
                <div className="pf-toj-finance-blockers" aria-label="Close blockers">
                  {blockers.length ? blockers.map(bucket => (
                    <span key={bucket.bucket} className={`pf-toj-blocker-pill pf-toj-blocker-${bucket.severity || "green"}`}>
                      <strong>{formatNumber(bucket.count || 0)}</strong>
                      {readableEventLabel(bucket.bucket)}
                    </span>
                  )) : (
                    <div className="pf-toj-empty">
                      <strong>No blockers showing.</strong>
                      <span>Close blockers appear here when finance review is needed.</span>
                    </div>
                  )}
                </div>
              </CardBody>
            </Card>
            <Card className="pf-toj-work-card pf-toj-finance-span">
              <CardBody>
                <Flex alignItems={{ default: "alignItemsCenter" }} justifyContent={{ default: "justifyContentSpaceBetween" }}>
                  <FlexItem>
                    <h2>Accounting Handoff</h2>
                    <p className="pf-toj-muted">{bridgeSummary}</p>
                  </FlexItem>
                  <FlexItem>
                    <Button variant="secondary" onClick={() => void exportBridgeEvents()} isDisabled={saving}>
                      Export IMS events
                    </Button>
                  </FlexItem>
                </Flex>
                <div className="pf-toj-finance-controls">
                  <FormGroup label="Rows to show">
                    <FormSelect aria-label="Accounting handoff filter" value={bridgeFilter} onChange={(_event, value) => setBridgeFilter(String(value))}>
                      <FormSelectOption value="open" label="Open accounting work" />
                      <FormSelectOption value="Ready" label="Ready" />
                      <FormSelectOption value="Exported" label="Sent to accounting" />
                      <FormSelectOption value="Imported" label="Imported by accounting" />
                      <FormSelectOption value="Posted" label="Posted in accounting" />
                      <FormSelectOption value="Reconciled" label="Reconciled" />
                      <FormSelectOption value="Blocked" label="Blocked" />
                      <FormSelectOption value="all" label="All handoff rows" />
                    </FormSelect>
                  </FormGroup>
                  <FormGroup label="Accounting response">
                    <FormSelect aria-label="Accounting response" value={bridgeStatus} onChange={(_event, value) => setBridgeStatus(String(value))}>
                      <FormSelectOption value="" label="Choose response" />
                      <FormSelectOption value="Exported" label="Sent to accounting" />
                      <FormSelectOption value="Imported" label="Imported by accounting" />
                      <FormSelectOption value="Posted" label="Posted in accounting" />
                      <FormSelectOption value="Reconciled" label="Reconciled in accounting" />
                      <FormSelectOption value="Blocked" label="Blocked by accounting" />
                    </FormSelect>
                  </FormGroup>
                  <FormGroup label="Reason if blocked">
                    <TextInput aria-label="Reason if blocked" value={blockedReason} onChange={(_event, value) => setBlockedReason(String(value))} placeholder="Only needed when blocked" />
                  </FormGroup>
                  <Button variant="primary" onClick={() => void applyBridgeStatus()} isDisabled={saving}>
                    Apply to selected
                  </Button>
                </div>
                <div className="pf-toj-table-wrap pf-toj-finance-table-wrap" tabIndex={0}>
                  <Table aria-label="Accounting handoff rows" variant="compact">
                    <Thead>
                      <Tr>
                        <Th>Select</Th>
                        <Th>Document</Th>
                        <Th>Event</Th>
                        <Th>Source</Th>
                        <Th>Status</Th>
                        <Th>Events</Th>
                        <Th>Created</Th>
                        <Th>Accounting Dates</Th>
                        <Th>Issue</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredBridgeRows.length ? filteredBridgeRows.map(row => (
                        <Tr key={row.id}>
                          <Td dataLabel="Select">
                            <input
                              type="checkbox"
                              checked={selectedBridgeIds.has(row.id)}
                              onChange={() => toggleBridgeRow(row.id)}
                              aria-label={`Select accounting handoff ${row.document_number || row.id}`}
                            />
                          </Td>
                          <Td dataLabel="Document"><strong>{row.document_number || ""}</strong></Td>
                          <Td dataLabel="Event">{readableEventLabel(row.event_type)}</Td>
                          <Td dataLabel="Source">{readableEventLabel(row.source_type)}</Td>
                          <Td dataLabel="Status"><Label color={financeStatusColor(row.status)}>{row.status || "Open"}</Label></Td>
                          <Td dataLabel="Events">{formatNumber(row.accounting_event_count || 0)}</Td>
                          <Td dataLabel="Created">{compactDateTime(row.created_at)}</Td>
                          <Td dataLabel="Accounting Dates">
                            <small>
                              {[row.exported_at && `Sent ${compactDateTime(row.exported_at)}`, row.imported_at && `Imported ${compactDateTime(row.imported_at)}`, row.posted_at && `Posted ${compactDateTime(row.posted_at)}`, row.reconciled_at && `Reconciled ${compactDateTime(row.reconciled_at)}`].filter(Boolean).join(" | ") || "Not returned yet"}
                            </small>
                          </Td>
                          <Td dataLabel="Issue">{row.blocked_reason || ""}</Td>
                        </Tr>
                      )) : (
                        <Tr><Td colSpan={9}>No accounting handoff rows match this view.</Td></Tr>
                      )}
                    </Tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
            <Card className="pf-toj-work-card">
              <CardBody>
                <h2>Period Lock</h2>
                <p className="pf-toj-muted">Lock or reopen reviewed inventory and StoreHub periods with a reason.</p>
                <Form className="pf-toj-form">
                  <div className="pf-toj-field-row">
                    <FormGroup label="Start">
                      <TextInput aria-label="Period start" type="date" value={periodStart} onChange={(_event, value) => setPeriodStart(String(value))} />
                    </FormGroup>
                    <FormGroup label="End">
                      <TextInput aria-label="Period end" type="date" value={periodEnd} onChange={(_event, value) => setPeriodEnd(String(value))} />
                    </FormGroup>
                    <FormGroup label="Scope">
                      <FormSelect aria-label="Period scope" value={lockScope} onChange={(_event, value) => setLockScope(String(value))}>
                        <FormSelectOption value="inventory" label="Inventory" />
                        <FormSelectOption value="storehub" label="StoreHub" />
                        <FormSelectOption value="all" label="All" />
                      </FormSelect>
                    </FormGroup>
                    <FormGroup label="Status">
                      <FormSelect aria-label="Period status" value={lockStatus} onChange={(_event, value) => setLockStatus(String(value))}>
                        <FormSelectOption value="Locked" label="Locked" />
                        <FormSelectOption value="Open" label="Open" />
                      </FormSelect>
                    </FormGroup>
                    <FormGroup label="Reason">
                      <TextInput aria-label="Period reason" value={lockReason} onChange={(_event, value) => setLockReason(String(value))} placeholder="Close note" />
                    </FormGroup>
                    <Button variant="primary" onClick={() => void savePeriodLock()} isDisabled={saving}>
                      Save period status
                    </Button>
                  </div>
                </Form>
                <div className="pf-toj-table-wrap pf-toj-finance-lock-wrap" tabIndex={0}>
                  <Table aria-label="Period lock history" variant="compact">
                    <Thead>
                      <Tr>
                        <Th>Start</Th>
                        <Th>End</Th>
                        <Th>Scope</Th>
                        <Th>Status</Th>
                        <Th>Reason</Th>
                        <Th>By</Th>
                        <Th>When</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {lockRows.length ? lockRows.map((row, index) => (
                        <Tr key={`${row.period_start}-${row.period_end}-${row.lock_scope}-${index}`}>
                          <Td dataLabel="Start">{compactDate(row.period_start)}</Td>
                          <Td dataLabel="End">{compactDate(row.period_end)}</Td>
                          <Td dataLabel="Scope">{readableEventLabel(row.lock_scope)}</Td>
                          <Td dataLabel="Status"><Label color={financeStatusColor(row.status)}>{row.status || ""}</Label></Td>
                          <Td dataLabel="Reason">{row.reason || ""}</Td>
                          <Td dataLabel="By">{row.created_by_name || ""}</Td>
                          <Td dataLabel="When">{compactDateTime(row.created_at)}</Td>
                        </Tr>
                      )) : (
                        <Tr><Td colSpan={7}>No period lock history yet.</Td></Tr>
                      )}
                    </Tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
            <Card className="pf-toj-work-card">
              <CardBody>
                <h2>Accounting Summary</h2>
                <p className="pf-toj-muted">Document totals by source, event, and accounting status.</p>
                <div className="pf-toj-table-wrap pf-toj-finance-lock-wrap" tabIndex={0}>
                  <Table aria-label="Accounting period summary" variant="compact">
                    <Thead>
                      <Tr>
                        <Th>Source</Th>
                        <Th>Event</Th>
                        <Th>Status</Th>
                        <Th>Documents</Th>
                        <Th>First Created</Th>
                        <Th>Last Created</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {summaryRows.length ? summaryRows.map((row, index) => (
                        <Tr key={`${row.source_type}-${row.event_type}-${row.status}-${index}`}>
                          <Td dataLabel="Source">{readableEventLabel(row.source_type)}</Td>
                          <Td dataLabel="Event">{readableEventLabel(row.event_type)}</Td>
                          <Td dataLabel="Status"><Label color={financeStatusColor(row.status)}>{row.status || ""}</Label></Td>
                          <Td dataLabel="Documents">{formatNumber(row.document_count || 0)}</Td>
                          <Td dataLabel="First Created">{compactDateTime(row.first_created_at)}</Td>
                          <Td dataLabel="Last Created">{compactDateTime(row.last_created_at)}</Td>
                        </Tr>
                      )) : (
                        <Tr><Td colSpan={6}>No accounting summary rows for this period.</Td></Tr>
                      )}
                    </Tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </div>
        )}
      </div>
    </PageSection>
  );
}
